import {
  AdminEventRuntimePiniaVueSdk,
  adminEventRuntimeApiSdk,
  useAdminEventActivityStore,
  useAdminItemBiddingStore,
  useEventEngineStateStore,
  useEventRuntimeEngineServiceInfoStore,
  useItemEngineStateStore,
} from '@hds/admin-client-pinia-vue-sdk';
import { socketCredentials, useRuntimeEndpoints } from '@hds/auto-auction-com-client-sdk';

import { useSaleAndAssetsStore } from './stores/sale-and-assets.store.js';

// The clerk's live connection to the event runtime — the delta-server change
// feed and the admin REST client behind it — created once per page load and
// pointed at one event at a time.
//
// It is pointed where `configureBackends` already pointed the runtime client:
// the base URIs and the Bearer header are read BACK from `adminEventRuntimeApiSdk`
// and the SDK's runtime endpoints, never from a manifest, an environment
// variable or a second table. One mechanism finds the runtime (backend.js over
// the client SDK's service discovery); this consumes what it found.
//
// The legacy clerk did this in its main.js, for one event named in the query
// string, before the app mounted. Here the console is a route, so the
// connection is made the first time a console opens and the event is watched
// and unwatched as consoles come and go.

/**
 * What the runtime SDK needs, read back from the configured client. Pure, so a
 * test can hold that an unconfigured runtime is refused rather than dialled.
 *
 * @param {Object} [source]
 * @param {Object} [source.apiSdk]    The admin runtime REST client; the configured singleton by default.
 * @param {Object} [source.endpoints] The SDK's runtime endpoints; what `configureBackends` set by default.
 */
export function runtimeSettings({ apiSdk = adminEventRuntimeApiSdk, endpoints = useRuntimeEndpoints() } = {}) {
  const settings = {
    biddingUrl: apiSdk.baseBiddingUri,
    deltaWebSocketUrl: endpoints.deltaWebSocketUrl,
    authHeader: apiSdk.customHeaderFn,
  };
  const missing = Object.entries(settings)
    .filter(([, value]) => !value)
    .map(([key]) => key);
  if (missing.length > 0) {
    throw new Error(
      `The event runtime is not configured (${missing.join(', ')}). configureBackends sets these from the manifest at boot.`
    );
  }
  return settings;
}

/** The real SDK over the settings. Exported so a test can hold that the settings reach it in the SDK's own argument order. */
export function createRuntime({ biddingUrl, deltaWebSocketUrl, authHeader }) {
  return new AdminEventRuntimePiniaVueSdk(biddingUrl, deltaWebSocketUrl, authHeader, socketCredentials);
}

let runtime = null;
let connecting = null;

/**
 * The connected runtime, made on first use. A failed connection is not
 * remembered, so the next console to open tries again.
 *
 * @param {Object} [options]
 * @param {Function} [options.create] Builds the SDK from the settings — tests hand in a fake.
 */
export function ensureClerkRuntime({ create = createRuntime } = {}) {
  if (connecting === null) {
    connecting = (async function () {
      // The admin stores exist before the change feeds push into them.
      useAdminEventActivityStore();
      useAdminItemBiddingStore();
      useEventEngineStateStore();
      useItemEngineStateStore();
      useEventRuntimeEngineServiceInfoStore();
      const sdk = create(runtimeSettings());
      await sdk.initialize();
      runtime = sdk;
      return sdk;
    })().catch((error) => {
      connecting = null;
      throw error;
    });
  }
  return connecting;
}

/** Load the event's sale and its assets, then follow the event and every asset in it. */
export async function openEvent(eventId, options) {
  const sdk = await ensureClerkRuntime(options);
  await useSaleAndAssetsStore().loadSaleAndAssets(eventId);
  await sdk.watchEvent(eventId);
  await sdk.watchItemsByEventDetailId(eventId);
}

/** Stop following an event. Nothing to do when no runtime was ever connected. */
export async function closeEvent(eventId) {
  if (runtime === null) return;
  await runtime.unwatchItemsByEventDetailId(eventId);
  await runtime.unwatchEvent(eventId);
}

/** Forget the connection. Tests only — one case's runtime must not leak into the next. */
export function resetClerkRuntime() {
  runtime = null;
  connecting = null;
}
